import { InteractionRequiredAuthError, PublicClientApplication, type AccountInfo, type AuthenticationResult, type Configuration } from '@azure/msal-browser';

interface AuthConfig {
  clientId: string;
  tenantId: string;
  apiScope: string;
}

const authConfig: AuthConfig = {
  clientId: import.meta.env.VITE_AAD_CLIENT_ID || '',
  tenantId: import.meta.env.VITE_AAD_TENANT_ID || '',
  apiScope: import.meta.env.VITE_API_SCOPE || ''
};

const isAuthEnabled = Boolean(authConfig.clientId && authConfig.tenantId && authConfig.apiScope);

let msalApp: PublicClientApplication | null = null;
let initialized = false;
let activeAccount: AccountInfo | null = null;
let reauthInFlight = false;

function getMsalConfiguration(): Configuration {
  return {
    auth: {
      clientId: authConfig.clientId,
      authority: `https://login.microsoftonline.com/${authConfig.tenantId}`,
      redirectUri: window.location.origin,
      postLogoutRedirectUri: window.location.origin
    },
    cache: {
      cacheLocation: 'sessionStorage'
    }
  };
}

function setActiveAccount(account: AccountInfo | null): void {
  activeAccount = account;
  if (msalApp && account) {
    msalApp.setActiveAccount(account);
  }
}

function getDefaultScopes(): string[] {
  return ['openid', 'profile', authConfig.apiScope];
}

export async function initializeAuth(): Promise<void> {
  if (!isAuthEnabled || initialized) {
    return;
  }

  msalApp = new PublicClientApplication(getMsalConfiguration());
  await msalApp.initialize();

  const redirectResult = await msalApp.handleRedirectPromise();
  if (redirectResult?.account) {
    setActiveAccount(redirectResult.account);
  }

  const accounts = msalApp.getAllAccounts();
  if (!activeAccount && accounts.length > 0) {
    setActiveAccount(accounts[0]);
  }

  initialized = true;
}

export async function ensureAuthenticated(): Promise<void> {
  if (!isAuthEnabled) {
    return;
  }

  if (!initialized) {
    await initializeAuth();
  }

  if (!msalApp) {
    throw new Error('Authentication client not initialized');
  }

  if (!activeAccount) {
    await msalApp.loginRedirect({
      scopes: getDefaultScopes()
    });
  }
}

export async function getAccessToken(forceRefresh = false): Promise<string | null> {
  if (!isAuthEnabled) {
    return null;
  }

  if (!initialized) {
    await initializeAuth();
  }

  if (!msalApp || !activeAccount) {
    return null;
  }

  try {
    const result: AuthenticationResult = await msalApp.acquireTokenSilent({
      account: activeAccount,
      scopes: [authConfig.apiScope],
      forceRefresh
    });
    return result.accessToken;
  } catch (error) {
    if (!(error instanceof InteractionRequiredAuthError)) {
      throw error;
    }

    const result: AuthenticationResult = await msalApp.ssoSilent({
      account: activeAccount,
      scopes: [authConfig.apiScope],
      loginHint: activeAccount.username
    });
    if (result.account) {
      setActiveAccount(result.account);
    }
    return result.accessToken;
  }
}

export async function triggerReauth(): Promise<void> {
  if (!isAuthEnabled || reauthInFlight) {
    return;
  }

  if (!initialized) {
    await initializeAuth();
  }

  if (!msalApp) {
    return;
  }

  reauthInFlight = true;

  const scopes = getDefaultScopes();
  if (activeAccount) {
    await msalApp.acquireTokenRedirect({ account: activeAccount, scopes });
  } else {
    await msalApp.loginRedirect({ scopes });
  }
}

export function getCurrentUserName(): string | null {
  if (!isAuthEnabled || !activeAccount) {
    return null;
  }

  return activeAccount.name || activeAccount.username || null;
}

export async function logout(): Promise<void> {
  if (!isAuthEnabled || !msalApp) {
    return;
  }

  await msalApp.logoutRedirect();
}

export function authEnabled(): boolean {
  return isAuthEnabled;
}
